import { Box, Center, Image, useBoolean } from '@chakra-ui/react';
import { memo } from 'react';

import { PAGE_THUMBNAIL_URL } from '../TemplatesMenu.config';
import { blurTemplateOverlayButtonIfActive } from '../TemplatesMenu.helpers';
import { AccessibleTemplateTag } from '../common/AccessibleTemplateTag';
import { ThumbnailOverlay } from '../common/ThumbnailOverlay';

/**
 * TEMPLATE PAGE PREVIEW
 *
 * Renders a single page from a multipage template.
 * Includes hover overlay - same as TemplateThumbnail
 */
interface TemplatePageProps {
  templateId: string;
  pageNumber: number;
  addPageIsDisabled: boolean;
  onSelectPage: (pageNumber: number) => void;
  isLoading: boolean;
  isAccessibleTemplate: boolean;
  pageIndexStr: string;
}

export const TemplatePage = memo(
  ({
    templateId,
    pageNumber,
    onSelectPage,
    addPageIsDisabled,
    isLoading,
    isAccessibleTemplate,
    pageIndexStr,
  }: TemplatePageProps) => {
    const [isHovered, setIsHovered] = useBoolean();

    const handleClick = () => onSelectPage(pageNumber);

    const thumbnailUrl = `${PAGE_THUMBNAIL_URL}${templateId}.${pageNumber}.png`;

    const handleMouseEnter = () => {
      setIsHovered.on();

      // Blur the active element to prevent multiple hover states
      blurTemplateOverlayButtonIfActive();
    };

    return (
      <Box
        w='full'
        h='fit-content'
        position='relative'
        borderRadius='base'
        onMouseEnter={handleMouseEnter}
        onMouseLeave={setIsHovered.off}
        onFocus={setIsHovered.on}
        onBlur={setIsHovered.off}
        border='1px solid'
        borderColor='gray.200'
        overflow='hidden'
        data-testid={`${templateId}-page-${pageNumber}`}
      >
        <Image src={thumbnailUrl} w='full' loading='lazy' />
        <ThumbnailOverlay
          show={isHovered}
          onClick={handleClick}
          userHasPermission={true}
          addPageIsDisabled={addPageIsDisabled}
          isLoading={isLoading}
          // Always false since this component renders single page from a multipage template
          isMultipage={false}
        />
        <Center
          fontSize='xs'
          color='white'
          bgColor='blackAlpha.800'
          w='12'
          h='5'
          position='absolute'
          borderRadius='10'
          bottom={1.5}
          left={1.5}
        >
          {pageIndexStr}
        </Center>
        {isAccessibleTemplate && <AccessibleTemplateTag />}
      </Box>
    );
  },
);
TemplatePage.displayName = 'TemplatePage';
